import React from 'react';
import { Progress, Space, Typography } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

interface ProgressBarProps {
  completed: number;
  total: number;
  showText?: boolean;
  showIcon?: boolean;
  size?: 'small' | 'default';
  strokeColor?: string;
  trailColor?: string;
  style?: React.CSSProperties;
  format?: 'percentage' | 'fraction' | 'both';
  status?: 'normal' | 'exception' | 'active' | 'success';
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  completed,
  total,
  showText = true,
  showIcon = false,
  size = 'default',
  strokeColor,
  trailColor,
  style,
  format = 'both',
  status
}) => {
  const { t } = useTranslation();
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  // Determine status based on progress if not explicitly provided
  const progressStatus = status || (percentage === 100 ? 'success' : 'normal');
  
  // Format text based on format prop
  const getProgressText = () => {
    switch (format) {
      case 'percentage':
        return `${percentage}%`;
      case 'fraction':
        return `${completed}/${total}`;
      case 'both':
        return `${completed}/${total} (${percentage}%)`;
      default:
        return `${percentage}%`;
    }
  };

  // Custom format function for Progress component
  const progressFormat = (_percent?: number) => {
    if (!showText) return '';
    return getProgressText();
  };

  return (
    <div style={style}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        {showIcon && (
          <Space size={4}>
            {progressStatus === 'success' ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <ClockCircleOutlined style={{ color: '#1890ff' }} />
            )}
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('planDetail.progress.status')}
            </Text>
          </Space>
        )}
        
        <Progress
          percent={percentage}
          size={size}
          status={progressStatus}
          strokeColor={strokeColor}
          trailColor={trailColor}
          format={progressFormat}
          showInfo={showText}
        />
        
        {showText && format === 'both' && (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '12px' }}>
            <Text type="secondary">{t('planDetail.milestone.completed')}: {completed}</Text>
            <Text type="secondary">{t('planDetail.subtask.total')}: {total}</Text>
          </div>
        )}
      </Space>
    </div>
  );
};

// Milestone progress component with specific styling
export const MilestoneProgress: React.FC<{
  milestones: Array<{ status: string }>;
  style?: React.CSSProperties;
}> = ({ milestones, style }) => {
  const completed = milestones.filter(m => m.status === 'Completed').length;
  const total = milestones.length;
  
  return (
    <ProgressBar
      completed={completed}
      total={total}
      showIcon={true}
      format="both"
      strokeColor="#1890ff"
      style={style}
    />
  );
};

// Subtask progress component with specific styling
export const SubtaskProgress: React.FC<{
  subtasks: Array<{ status: string }>;
  style?: React.CSSProperties;
}> = ({ subtasks, style }) => {
  const completed = subtasks.filter(s => s.status === 'Completed').length;
  const total = subtasks.length;
  
  return (
    <ProgressBar
      completed={completed}
      total={total}
      showIcon={false}
      format="fraction"
      size="small"
      strokeColor="#52c41a"
      style={style}
    />
  );
};

// Overall plan progress component
export const PlanProgress: React.FC<{
  milestones: Array<{ status: string; subtasks?: Array<{ status: string }> }>;
  style?: React.CSSProperties;
}> = ({ milestones, style }) => {
  const { t } = useTranslation();
  // Calculate overall progress based on both milestones and subtasks
  const totalMilestones = milestones.length;
  const completedMilestones = milestones.filter(m => m.status === 'Completed').length;
  
  const totalSubtasks = milestones.reduce((sum, m) => sum + (m.subtasks?.length || 0), 0);
  const completedSubtasks = milestones.reduce((sum, m) => 
    sum + (m.subtasks?.filter(s => s.status === 'Completed').length || 0), 0
  );
  
  // Weight milestones and subtasks equally for overall progress
  const milestoneProgress = totalMilestones > 0 ? (completedMilestones / totalMilestones) * 50 : 0;
  const subtaskProgress = totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 50 : 0;
  const overallProgress = Math.round(milestoneProgress + subtaskProgress);
  
  return (
    <div style={style}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Text strong>{t('planDetail.progress.overallProgress')}</Text>
        <Progress
          percent={overallProgress}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
          format={(percent) => `${percent}%`}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: '12px' }}>
          <Text type="secondary">
            {t('planDetail.progress.milestones')}: {completedMilestones}/{totalMilestones}
          </Text>
          <Text type="secondary">
            {t('planDetail.progress.subtasks')}: {completedSubtasks}/{totalSubtasks}
          </Text>
        </div>
      </Space>
    </div>
  );
};

export default ProgressBar;